import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Container,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from 'src/icons/Search';
import ClearIcon from '@mui/icons-material/Clear';

const SimpleSearchBar = ({ searchParams, setSearchParams, setPage, compact = false }) => {
  const [searchString, setSearchString] = useState(searchParams ?? '');
  const inputRef = useRef(null);

  // Global `/` shortcut focuses the search input. Skip when the user is already
  // typing in another text-like field so it doesn't hijack their keystroke.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/') return;
      const active = document.activeElement;
      const tag = active?.tagName;
      const isTyping =
        tag === 'INPUT' || tag === 'TEXTAREA' || active?.isContentEditable;
      if (isTyping) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleSearch = () => {
    setPage(0);
    setSearchParams(searchString);
  };

  const handleClear = () => {
    setSearchString('');
    setSearchParams('');
  };

  return (
    <Container maxWidth={compact ? false : 'md'} disableGutters={compact}>
      <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
        <Tooltip title="Press / to focus" placement="top">
          <TextField
            fullWidth
            size={compact ? 'small' : 'medium'}
            sx={{ minWidth: 0 }}
            inputRef={inputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchString ? (
                <InputAdornment position="end">
                  <Button
                    color="primary"
                    onClick={handleClear}
                    size={compact ? 'small' : 'large'}
                    sx={{ ml: compact ? 0 : 2, minWidth: compact ? 28 : undefined }}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ) : null,
            }}
            onChange={(event) => setSearchString(event.target.value)}
            onKeyUp={handleKeyUp}
            placeholder="Search..."
            value={searchString}
          />
        </Tooltip>
        <Button
          color="primary"
          onClick={handleSearch}
          size={compact ? 'medium' : 'large'}
          sx={{ ml: compact ? 1 : 2, flexShrink: 0 }}
          variant="contained"
        >
          Search
        </Button>
      </Box>
    </Container>
  );
};

SimpleSearchBar.propTypes = {
  searchParams: PropTypes.string,
  setSearchParams: PropTypes.func.isRequired,
  setPage: PropTypes.func,
  compact: PropTypes.bool,
};

export default SimpleSearchBar;
